import {
  PlanTypeV4,
  AccountData,
  Conversation,
  DataFilterType,
  FollowerStatus,
  TwilioPhoneVerificationStatus,
} from "@markit/common.types";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Colors } from "../../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import ConversationsSidebarPreview from "./ConversationsSidebarPreview";
import ConversationsMainView from "./ConversationsMainView";
import filter from "lodash.filter";
import {
  hasSubscription,
  isSubscriptionPaymentFailed,
  userPhoneVerificationStatus,
} from "@markit/common.utils";
import { ConversationSidebarEmptyState } from "./ConversationSidebarEmptyState";
import BlurredConversationPreview from "../../../assets/FreeTierConversationBlurred.png";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import { EmptyStateFlatlist } from "../../EmptyStates/EmptyStateFlatlist";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { useLoadUserFollowList } from "../../../hooks/useLoadUserFollowList";
import {
  ConversationStatus,
  useLoadUserConversationsList,
} from "../../../hooks/useLoadUserConversationsList";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import { useNavigate } from "react-router-dom";
import { NavigationId } from "../../../navigation/AppParamList";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import VirtuosoList from "../../Lists/VirtuosoList";
import ListTabs from "../../DropdownsAndTabs/ListTabs";
import ConversationFilterModal from "./ConversationFilterModal";
import ConversationInboxSettingsDropdown from "./ConversationInboxSettingsDropdown";

export enum ConversationOption {
  ALL = 0,
  UNREAD = 1,
  ARCHIVED = 2,
}

type ConversationInboxPanelProps = {
  panelTabValue: number;
  setPanelTabValue: (tabValue: number) => void;
  newConversation: boolean;
  setNewConversation: (newConversation: boolean) => void;
};

const ConversationInboxPanel = (props: ConversationInboxPanelProps) => {
  const {
    panelTabValue,
    setPanelTabValue,
    newConversation,
    setNewConversation,
  } = props;
  const { accountData, followersData, followingAccountData, dataFilters } =
    useSelector(getAccountSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConversationUser, setSelectedConversationUser] =
    useState<AccountData>();
  const [hoveredConversationIndex, setHoveredConversationIndex] = useState<
    number | null
  >(null);
  const [newUserSearchTerm, setNewUserSearchTerm] = useState("");
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [loadingInitialConversation, setLoadingInitialConversation] =
    useState(true);
  const [tabValue, setTabValue] = useState<ConversationOption>(
    ConversationOption.ALL
  );
  const [filteredConversations, setFilteredConversations] = useState<
    Conversation[]
  >([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);

  const styles = {
    optionContainer: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      cursor: "pointer",
      border: `0.5px solid ${Colors.GRAY2}`,
    },
  };

  const accountStatus = useMemo(
    () => userPhoneVerificationStatus(accountData),
    [accountData]
  );

  const approvedAccountStatus = useMemo(
    () =>
      hasSubscription(accountData) &&
      accountStatus === TwilioPhoneVerificationStatus.APPROVED,
    [accountData, accountStatus]
  );

  const {
    fetchedConversations: allConversations,
    fetchedUserData: allUserData,
    fetchedFollowerData: allFollowerData,
    isLoading: allIsLoading,
    isFinished: allIsFinished,
    loadSearchResultsThrottled: loadAllConversationResultsThrottled,
    fetchConversations: fetchAllConversations,
    fetchMoreConversations: fetchMoreAllConversations,
  } = useLoadUserConversationsList({
    userId: accountData.uid,
    windowSize: 20,
    status: ConversationStatus.ALL,
  });

  const {
    fetchedConversations: unreadConversations,
    fetchedUserData: unreadUserData,
    fetchedFollowerData: unreadFollowerData,
    isLoading: unreadIsLoading,
    isFinished: unreadIsFinished,
    loadSearchResultsThrottled: loadUnreadConversationResultsThrottled,
    fetchConversations: fetchUnreadConversations,
    fetchMoreConversations: fetchMoreUnreadConversations,
    resetLoadedUsers: resetUnreadUsers,
  } = useLoadUserConversationsList({
    userId: accountData.uid,
    windowSize: 20,
    status: ConversationStatus.UNREAD,
  });

  const {
    fetchedConversations: archivedConversations,
    fetchedUserData: archivedUserData,
    fetchedFollowerData: archivedFollowerData,
    isLoading: archivedIsLoading,
    isFinished: archivedIsFinished,
    loadSearchResultsThrottled: loadArchivedConversationResultsThrottled,
    fetchConversations: fetchArchivedConversations,
    fetchMoreConversations: fetchMoreArchivedConversations,
  } = useLoadUserConversationsList({
    userId: accountData.uid,
    windowSize: 20,
    status: ConversationStatus.ARCHIVED,
  });

  const {
    fetchedUserData: fetchedFollowersUserData,
    loadSearchResultsThrottled,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: FollowerStatus.SUBSCRIBED,
    windowSize: 10,
  });

  const foundConversationFilter = useMemo(
    () =>
      dataFilters.find(
        (filter) => filter.type === DataFilterType.CONVERSATIONS
      ),
    [dataFilters]
  );

  const filteredConversationsToShow = useMemo(() => {
    if (tabValue === ConversationOption.UNREAD) {
      return filteredConversations.filter((conversation) =>
        conversation.chatIndicator.some(
          (indicator) =>
            indicator.uid === accountData.uid && indicator.indicator
        )
      );
    } else if (tabValue === ConversationOption.ARCHIVED) {
      return filteredConversations.filter(
        (conversation) => conversation.archived
      );
    } else {
      return filteredConversations.filter(
        (conversation) => !conversation.archived
      );
    }
  }, [filteredConversations, tabValue, accountData.uid]);

  const fetchedConversations = useMemo(() => {
    return foundConversationFilter
      ? filteredConversationsToShow
      : tabValue === ConversationOption.UNREAD
        ? unreadConversations
        : tabValue === ConversationOption.ARCHIVED
          ? archivedConversations
          : allConversations;
  }, [
    foundConversationFilter,
    filteredConversationsToShow,
    tabValue,
    unreadConversations,
    archivedConversations,
    allConversations,
  ]);

  const fetchedUserData = useMemo(() => {
    return foundConversationFilter
      ? followingAccountData
      : tabValue === ConversationOption.UNREAD
        ? unreadUserData
        : tabValue === ConversationOption.ARCHIVED
          ? archivedUserData
          : allUserData;
  }, [
    foundConversationFilter,
    followingAccountData,
    tabValue,
    unreadUserData,
    archivedUserData,
    allUserData,
  ]);

  const fetchedFollowerData = useMemo(() => {
    return foundConversationFilter
      ? followersData
      : tabValue === ConversationOption.UNREAD
        ? unreadFollowerData
        : tabValue === ConversationOption.ARCHIVED
          ? archivedFollowerData
          : allFollowerData;
  }, [
    foundConversationFilter,
    followersData,
    tabValue,
    unreadFollowerData,
    archivedFollowerData,
    allFollowerData,
  ]);

  const isLoading = useMemo(() => {
    return tabValue === ConversationOption.UNREAD
      ? unreadIsLoading
      : tabValue === ConversationOption.ARCHIVED
        ? archivedIsLoading
        : allIsLoading;
  }, [tabValue, unreadIsLoading, allIsLoading, archivedIsLoading]);

  const isFinished = useMemo(() => {
    return (
      (tabValue === ConversationOption.UNREAD
        ? unreadIsFinished
        : tabValue === ConversationOption.ARCHIVED
          ? archivedIsFinished
          : allIsFinished) || !!foundConversationFilter
    );
  }, [
    tabValue,
    unreadIsFinished,
    allIsFinished,
    archivedIsFinished,
    foundConversationFilter,
  ]);

  const selectedConversation = useMemo(
    () =>
      fetchedConversations.find(
        (convo) =>
          selectedConversationUser && convo.uid === selectedConversationUser.uid
      ),
    [selectedConversationUser, fetchedConversations]
  );

  // This is to help update the local state of the filtered conversations since there is no snapshot listener for these
  const updateFilteredConversationLocally = useCallback(
    (updatedConversation: Conversation) => {
      if (!foundConversationFilter) {
        return;
      }

      setFilteredConversations((prev) => {
        const foundConversation = prev.find(
          (conversation) => conversation.uid === updatedConversation.uid
        );
        if (!foundConversation) {
          return prev;
        }
        return prev.map((conversation) =>
          conversation.uid === updatedConversation.uid
            ? updatedConversation
            : conversation
        );
      });
    },
    [foundConversationFilter]
  );

  const isNewConversationNoneSelected = useMemo(
    () => newConversation && !selectedConversation,
    [newConversation, selectedConversation]
  );

  useAsyncOnMount(async () => {
    if (approvedAccountStatus) {
      await Promise.all([
        fetchAllConversations(),
        fetchArchivedConversations(),
      ]);
    }
    setIsLoadingConversations(false);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (approvedAccountStatus) {
      if (tabValue === ConversationOption.UNREAD) {
        await fetchUnreadConversations();
      } else {
        resetUnreadUsers();
      }
    }
  }, [
    tabValue,
    fetchUnreadConversations,
    resetUnreadUsers,
    approvedAccountStatus,
  ]);

  // initialize to start with the most recent conversation selected
  useEffect(() => {
    if (fetchedConversations.length > 0 && loadingInitialConversation) {
      const conversationUserData = fetchedUserData.find(
        (follower) => follower.uid === fetchedConversations[0].uid
      );
      if (conversationUserData) {
        setSelectedConversationUser(conversationUserData);
        setLoadingInitialConversation(false);
      }
    }
  }, [fetchedUserData, loadingInitialConversation, fetchedConversations]);

  const handleSearch = (text: string) => {
    if (text !== "") {
      if (tabValue === ConversationOption.UNREAD) {
        loadUnreadConversationResultsThrottled(text.toLowerCase());
      } else if (tabValue === ConversationOption.ARCHIVED) {
        loadArchivedConversationResultsThrottled(text.toLowerCase());
      } else {
        loadAllConversationResultsThrottled(text.toLowerCase());
      }
    }
    setSearchTerm(text.toLowerCase());
  };

  const containsUser = useCallback(
    (item: Conversation, query: string) => {
      const foundUser = fetchedUserData.find((user) => user.uid === item.uid);
      if (foundUser) {
        return foundUser.fullName.toLowerCase().includes(query);
      }
      return false;
    },
    [fetchedUserData]
  );

  const conversationsToShow = useMemo(() => {
    const sortedConversations = fetchedConversations.sort(
      (x: Conversation, y: Conversation) => {
        return (
          new Date(y.recentTimestamp).getTime() -
          new Date(x.recentTimestamp).getTime()
        );
      }
    );
    if (searchTerm === "") {
      return sortedConversations;
    }
    let conversationList: Conversation[] = sortedConversations;
    conversationList = filter(conversationList, (convo: Conversation) => {
      return containsUser(convo, searchTerm);
    });

    return conversationList;
  }, [fetchedConversations, searchTerm, containsUser]);

  const newConversationOnPress = useCallback(() => {
    if (hasSubscription(accountData)) {
      setNewConversation(true);
    } else {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name)
      );
    }
  }, [accountData, dispatch, setNewConversation]);

  useEffect(() => {
    // Whenever newConversation is true, set proper states to show a new chat
    if (newConversation) {
      setTabValue(ConversationOption.ALL);
      setNewUserSearchTerm("");
      setSelectedConversationUser(undefined);
    }
  }, [
    newConversation,
    setTabValue,
    setNewUserSearchTerm,
    setSelectedConversationUser,
  ]);

  const closeNewConversationOnPress = useCallback(() => {
    setNewUserSearchTerm("");
    setNewConversation(false);
    const conversationUserData = fetchedUserData.find(
      (follower) => follower.uid === fetchedConversations[0].uid
    );
    setSelectedConversationUser(conversationUserData);
  }, [setNewConversation, fetchedUserData, fetchedConversations]);

  const displayConversationsMainView = useCallback(
    async (item: Conversation) => {
      if (isSubscriptionPaymentFailed(accountData.customer.state)) {
        // setPaymentInfoVisible(true);
        // @TODO: Show modal about payment info
      } else {
        const mainUser = fetchedUserData.find((user) => user.uid === item.uid);

        setSelectedConversationUser(mainUser);
        setNewConversation(false);
      }
    },
    [accountData.customer.state, fetchedUserData, setNewConversation]
  );

  const renderFreeTierMessageTitle = useMemo(() => {
    if (accountData.numFreeTierReplies > 1) {
      // 2 or more people have tried to message free tier user
      return `${accountData.numFreeTierReplies} people texted you`;
    } else if (accountData.numFreeTierReplies === 1) {
      return "Someone texted you"; // 1 person tried to message
    }
    // 0 people have tried to message free tier user
    return "When someone texts you";
  }, [accountData.numFreeTierReplies]);

  const renderEmptyFree = useMemo(() => {
    return (
      <div className="ColumnNormal" style={{ paddingTop: 14 }}>
        <div style={{ marginBottom: 4 }}>
          <ConversationSidebarEmptyState
            title={renderFreeTierMessageTitle}
            subtext={`You can chat with and answer people's questions ${
              hasSubscription(accountData) && !approvedAccountStatus
                ? "once your account is approved."
                : "by upgrading to Markit+"
            }
              `}
            onPress={() => {
              dispatch(
                notificationActions.setMarkitPlusModal(
                  PlanTypeV4.ESSENTIAL.name
                )
              );
            }}
          />
        </div>
        {accountData.numFreeTierReplies > 0 ||
        (hasSubscription(accountData) && !approvedAccountStatus) ? (
          <img
            src={BlurredConversationPreview}
            alt="Blurred conversation list preview for free tier"
            style={{ width: "fit-content", alignSelf: "center" }}
          />
        ) : null}
      </div>
    );
  }, [
    renderFreeTierMessageTitle,
    accountData,
    approvedAccountStatus,
    dispatch,
  ]);

  const renderEmptySubView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoading}
        containerStyles={{ paddingTop: 120 }}
        nonSearchEmptyView={
          hasSubscription(accountData) && !approvedAccountStatus ? (
            renderEmptyFree
          ) : !newConversation ? (
            <ConversationSidebarEmptyState
              title={`No ${
                tabValue === ConversationOption.UNREAD
                  ? "Unread"
                  : tabValue === ConversationOption.ARCHIVED
                    ? "Archived"
                    : ""
              } Conversations`}
              subtext={`Your ${
                tabValue === ConversationOption.UNREAD
                  ? "unread"
                  : tabValue === ConversationOption.ARCHIVED
                    ? "archived"
                    : ""
              } conversations will show up here.`}
            />
          ) : (
            <></>
          )
        }
      />
    );
  }, [
    searchTerm,
    isLoading,
    accountData,
    approvedAccountStatus,
    renderEmptyFree,
    newConversation,
    tabValue,
  ]);

  const renderConversationItem = useCallback(
    (item: Conversation, index: number) => {
      const isSelected = item.uid === selectedConversation?.uid;
      const isHovered = hoveredConversationIndex === index;
      const isBelowSelectedOrHovered =
        (selectedConversation &&
          index ===
            conversationsToShow.findIndex(
              (c) => c.uid === selectedConversation.uid
            ) -
              1) ||
        (hoveredConversationIndex !== null &&
          index === hoveredConversationIndex - 1);
      return (
        <div className="ColumnNormal" key={item.uid}>
          <ConversationsSidebarPreview
            fetchedConversationUserData={fetchedUserData}
            fetchedConversationFollowerData={fetchedFollowerData}
            conversationIndex={index}
            conversationItem={item}
            isSelectedItem={isSelected}
            selectConversationMainView={displayConversationsMainView}
            hoveredIndex={hoveredConversationIndex}
            setHoveredIndex={setHoveredConversationIndex}
            onLocalConversationUpdate={updateFilteredConversationLocally}
          />
          <div style={{ width: 316, alignSelf: "flex-end" }}>
            <HorizontalDivider
              altColor={
                isSelected || isHovered || isBelowSelectedOrHovered
                  ? Colors.TRANSPARENT
                  : undefined
              }
            />
          </div>
        </div>
      );
    },
    [
      conversationsToShow,
      displayConversationsMainView,
      fetchedFollowerData,
      fetchedUserData,
      hoveredConversationIndex,
      updateFilteredConversationLocally,
      selectedConversation,
    ]
  );

  return panelTabValue === 0 ? (
    isLoadingConversations ||
    (fetchedConversations.length > 0 && loadingInitialConversation) ? (
      <div className="Centering" style={{ height: "80vh" }}>
        <CircularProgress style={{ color: Colors.BLACK }} size={30} />
      </div>
    ) : (
      <div
        className="RowNormal"
        style={{
          backgroundColor: Colors.WHITE,
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Conversations Sidebar */}
        <div
          className="ConversationsPanelLeftContainer ColumnNormal"
          style={{ height: "100%" }}
        >
          <div
            className="ColumnNormal"
            style={{
              gap: 14,
              padding: 14,
              paddingTop: 20,
              borderBottom: `${
                hasSubscription(accountData) &&
                !approvedAccountStatus &&
                conversationsToShow.length === 0
                  ? "0.5px"
                  : "0px"
              } solid ${Colors.GRAY11}`,
            }}
          >
            <div className="AlignedRow" style={{ gap: 10 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginBlock: 0 }}
                disabled={!approvedAccountStatus}
              />
              {hasSubscription(accountData) ? (
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <div
                    className="Centering"
                    style={{
                      ...styles.optionContainer,
                      backgroundColor: foundConversationFilter
                        ? Colors.BLUE5
                        : Colors.WHITE1,
                    }}
                    onClick={() => setShowFilterModal(true)}
                  >
                    <Icon
                      icon="ion:filter"
                      height={22}
                      style={{
                        color: foundConversationFilter
                          ? Colors.WHITE
                          : Colors.BLACK,
                      }}
                    />
                  </div>
                  <ConversationInboxSettingsDropdown
                    hasNonArchivedConversations={allConversations.length > 0}
                    hasAnyConversations={
                      allConversations.length > 0 ||
                      archivedConversations.length > 0
                    }
                  />
                </div>
              ) : null}
            </div>
            {approvedAccountStatus && !isNewConversationNoneSelected ? (
              <ListTabs
                tabLabels={["All", "Unread", "Archived"]}
                tabNumbers={[-1, -1, -1]}
                selectedValue={tabValue}
                onChange={setTabValue}
                containerStyles={{ flex: 1 }}
                tabStyles={{ flex: 1, justifyContent: "center" }}
                selectedTabStyles={{ flex: 1, justifyContent: "center" }}
                altColor={Colors.BLACK}
              />
            ) : null}
          </div>
          <div
            className="HideScrollbar AllowScroll"
            style={{ paddingInline: 14, height: "100%" }}
          >
            {isNewConversationNoneSelected ? (
              <div
                className="AlignedRowSpaced ConversationSidebarItem"
                style={{ backgroundColor: Colors.BLUE5, marginBottom: 4 }}
              >
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <Icon
                    icon={"mdi:person-circle-outline"}
                    height={50}
                    style={{ color: Colors.WHITE }}
                  />
                  <div style={{ height: 61 }}>
                    <span
                      className="sectionTitle"
                      style={{ color: Colors.WHITE }}
                    >
                      New Conversation
                    </span>
                  </div>
                </div>
                <Icon
                  onClick={closeNewConversationOnPress}
                  icon={"mdi:close"}
                  height={20}
                  style={{
                    color: Colors.WHITE,
                    pointerEvents:
                      fetchedConversations.length > 0 ? "all" : "none",
                  }}
                />
              </div>
            ) : null}
            {loadingFilter ? (
              <div className="Centering" style={{ height: "100%" }}>
                <CircularProgress style={{ color: Colors.BLACK }} size={30} />
              </div>
            ) : (
              <VirtuosoList
                items={conversationsToShow}
                renderItem={(item, index) => {
                  return renderConversationItem(item, index);
                }}
                isLoading={isLoading || loadingFilter}
                isFinished={isFinished}
                searchTerm={searchTerm}
                renderNonSearchEmptyView={
                  !hasSubscription(accountData)
                    ? renderEmptyFree
                    : renderEmptySubView
                }
                renderHeader={null}
                loadMoreOnPress={() => {
                  if (approvedAccountStatus) {
                    tabValue === ConversationOption.UNREAD
                      ? fetchMoreUnreadConversations()
                      : tabValue === ConversationOption.ARCHIVED
                        ? fetchMoreArchivedConversations()
                        : fetchMoreAllConversations();
                  }
                }}
              />
            )}
          </div>
        </div>
        {/* Conversations Main View */}
        <div
          className="ConversationsPanelRightContainer"
          style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
        >
          {(conversationsToShow.length === 0 && !newConversation) ||
          !approvedAccountStatus ? (
            <div className="Centering" style={{ height: "100%" }}>
              {isLoading || loadingFilter ? null : (
                <EmptyStateButton
                  title={
                    hasSubscription(accountData) ? (
                      !approvedAccountStatus ? (
                        accountStatus ===
                        TwilioPhoneVerificationStatus.REJECTED ? (
                          "Your account was rejected. Conversations unavailable."
                        ) : (
                          "Conversations unavailable while account is in review"
                        )
                      ) : foundConversationFilter &&
                        filteredConversationsToShow.length === 0 ? (
                        "No Conversations Found in Filter"
                      ) : fetchedConversations.length === 0 ? (
                        tabValue === ConversationOption.UNREAD ? (
                          "No Unread Conversations"
                        ) : tabValue === ConversationOption.ARCHIVED ? (
                          "No Archived Conversations"
                        ) : (
                          "You have no conversations yet"
                        )
                      ) : (
                        "No Conversations Found"
                      )
                    ) : (
                      <span>
                        Available with Markit
                        <span style={{ color: Colors.BLUE5 }}>+ </span>
                        Essential Plan
                      </span>
                    )
                  }
                  description={
                    tabValue === ConversationOption.ALL
                      ? hasSubscription(accountData) && !approvedAccountStatus
                        ? accountStatus ===
                          TwilioPhoneVerificationStatus.REJECTED
                          ? "This could be a result of invalid or incomplete account information. Contact support to resubmit."
                          : "This process can take 3-5 days. Contact support with questions or if there is an issue with your account."
                        : "Conversations are created when your subscribers message you."
                      : ""
                  }
                  btnText={
                    tabValue === ConversationOption.ALL
                      ? hasSubscription(accountData)
                        ? !approvedAccountStatus
                          ? "View Account Status"
                          : "New Chat"
                        : "Get Essential Plan"
                      : ""
                  }
                  onPress={
                    hasSubscription(accountData) && !approvedAccountStatus
                      ? () => navigate(NavigationId.HOME_ACCOUNT_STATUS)
                      : newConversationOnPress
                  }
                  icon={
                    <Icon
                      icon={
                        tabValue === ConversationOption.UNREAD
                          ? "ion:chatbubble-ellipses"
                          : tabValue === ConversationOption.ARCHIVED
                            ? "ion:archive"
                            : "ion:chatbubbles"
                      }
                      height={50}
                      style={{ color: Colors.BLACK }}
                    />
                  }
                  iconBox={70}
                  iconBackgroundColor={Colors.WHITE}
                />
              )}
            </div>
          ) : (
            <div key={selectedConversationUser?.uid}>
              <ConversationsMainView
                conversationUser={selectedConversationUser}
                setConversationUser={setSelectedConversationUser}
                fetchedConversationUserData={fetchedUserData}
                fetchedConversations={fetchedConversations}
                newUserSearchTerm={newUserSearchTerm}
                setNewUserSearchTerm={setNewUserSearchTerm}
                setNewConversation={setNewConversation}
                fetchedFollowers={fetchedFollowersUserData}
                fetchedFollowerData={fetchedFollowerData.find(
                  (follower) => follower.uid === selectedConversationUser?.uid
                )}
                loadSearchResultsThrottled={loadSearchResultsThrottled}
                addTextPreset={() => setPanelTabValue(1)}
                onLocalConversationUpdate={updateFilteredConversationLocally}
              />
            </div>
          )}
        </div>
        <ConversationFilterModal
          showModal={showFilterModal}
          setShowModal={setShowFilterModal}
          setFilteredConversations={setFilteredConversations}
          loadingFilter={loadingFilter}
          setLoadingFilter={setLoadingFilter}
        />
      </div>
    )
  ) : null;
};

export default memo(ConversationInboxPanel);
